/* eslint-disable */

/**
 * Write a function: func convertNodeTree(inputFormat) --> Array<outputNodeTree>
 * inputFormat is like this:
 * {
 *   "nodeID1": {
 *     "type": "type of the node",
 *     "data": { ... }, // JSON object, the schema varies with the type
 *     "children": [ ... ] // list of child node IDs
 *   },
 *   "nodeID2": {
 *     // same as above
 *   }
 * }
 *
 * the function needs to find all the nodes with type == 'scenarios',
 * and extract the node trees that start with those nodes
 * the outputNodeTree should be a root node that looks like this:
 * {
 *   "type": "scenarios",
 *   "payload": {...},
 *   "children": [
 *     {...}, // nested node
 *     {...}, // nested node
 *   ]
 * }
 *
 * [Example]
 * Input:
 * {
 *   "1": {
 *     "name": "",
 *     "type": "scenarios",
 *     "data": {
 *       "meta": {
 *         "endTime": "2029-11-17T16:00:00.000Z",
 *         "startTime": "2019-11-13T16:00:00.000Z"
 *       },
 *       "scenarios": [
 *         {
 *           "condition": {
 *             "conditions": [
 *               {
 *                 "lhs": "var.passenger.ID",
 *                 "operator": "eq",
 *                 "rhs": 91718782
 *               },
 *               {
 *                 "lhs": "var.food.cutlery.option",
 *                 "operator": "eq",
 *                 "rhs": 1
 *               }
 *             ],
 *             "operator": "and"
 *           },
 *           "eventType": "onFoodOrderComplete"
 *         }
 *       ]
 *     },
 *     "children": [
 *       "2"
 *     ]
 *   },
 *   "2": {
 *     "name": "",
 *     "type": "actions",
 *     "data": [
 *       {
 *         "meta": {
 *           "statsKey": "gandalfTest"
 *         },
 *         "type": "incrementUserStats"
 *       }
 *     ],
 *     "children": null
 *   }
 * }
 *
 * Output:
 * {
 *   "type": "scenarios",
 *   "payload": {
 *     "meta": {
 *       "endTime": "2029-11-17T16:00:00.000Z",
 *       "startTime": "2019-11-13T16:00:00.000Z"
 *     },
 *     "scenarios": [
 *       {
 *         "condition": {
 *           "conditions": [
 *             {
 *               "lhs": "var.passenger.ID",
 *               "operator": "eq",
 *               "rhs": 91718782
 *             },
 *             {
 *               "lhs": "var.food.cutlery.option",
 *               "operator": "eq",
 *               "rhs": 1
 *             }
 *           ],
 *           "operator": "and"
 *         },
 *         "eventType": "onFoodOrderComplete"
 *       }
 *     ]
 *   },
 *   "children": [
 *     {
 *       "type": "actions",
 *       "payload": {
 *         "data": [
 *           {
 *             "meta": {
 *               "statsKey": "gandalfTest"
 *             },
 *             "type": "incrementUserStats"
 *           }
 *         ]
 *       }
 *     }
 *   ]
 * }
 */

import { visualNodeTypes } from 'data/visualiser';

type InputNode = {
  name?: string,
  type: string,
  data: any,
  children: string[] | null,
};

type OutputNode = {
  type: string,
  payload: any,
  children?: OutputNode[],
  trackingParams?: TrackingParams,
};

type VisualNode = {
    type: string,
    payload: any,
    children?: VisualNode[],
    linkedCampaigns: CampaignMeta[],
}

type TrackingParams = {
    campaignID: string, // this should come from campaignMeta.campaignID
    countryID: number, // this should come from campaignMeta.countryID
    campaignName: string, // this should come from campaignMeta.name
}

type CampaignMeta = {
    campaignID: string,
    countryID: number,
    campaignName: string,
}

type ListNode = {
    type: string,
    payload: any,
    next?: ListNode,
    trackingParams?: TrackingParams,
}

type Campaign = {
    campaignID: string,
    countryID: number,
    name: string,
    originalNodes: { [key: string]: InputNode }
}

type MergeOptions = {
  mergeConditionsByLHS: boolean
}

type VisualiseOptions = {
  mergeConditionsByLHS: boolean
}

/**
 * Converts flat node structure to nested tree structure for scenarios nodes
 * @param inputFormat - Object with node IDs as keys and node data as values
 * @returns Array of output node trees starting from scenarios nodes
 */
export function convertNodeTree(inputFormat: { [key: string]: InputNode }, campaignMeta: CampaignMeta): OutputNode[] {
  const result: OutputNode[] = [];

  // Find all nodes with type === 'scenarios'
  for (const nodeId in inputFormat) {
    const node = inputFormat[nodeId];
    if (node.type === 'scenarios') {
      // Build the tree starting from this scenarios node
      const outputTree = buildNodeTree(nodeId, inputFormat, campaignMeta);
      if (outputTree) {
        result.push(outputTree);
      }
    }
  }

  return result;
}

/**
 * Recursively builds a node tree from the flat input format
 * @param nodeId - The ID of the current node to process
 * @param inputFormat - The flat input format object
 * @param campaignMeta - Campaign metadata for tracking params
 * @returns The converted node tree or null if node doesn't exist
 */
function buildNodeTree(nodeId: string, inputFormat: { [key: string]: InputNode }, campaignMeta: CampaignMeta): OutputNode | null {
  const node = inputFormat[nodeId];
  if (!node) return null;

  const outputNode: OutputNode = {
    type: node.type,
    payload: node.data,
    trackingParams: {
      campaignID: campaignMeta.campaignID,
      countryID: campaignMeta.countryID,
      campaignName: campaignMeta.campaignName
    }
  };

  // Process children if they exist
  if (node.children && Array.isArray(node.children) && node.children.length > 0) {
    const children = node.children
      .map(childId => buildNodeTree(childId, inputFormat, campaignMeta))
      .filter(child => child !== null);

    if (children.length > 0) {
      outputNode.children = children;
    }
  }

  return outputNode;
}

/*
Each scenarios node has a list of scenarios in `payload.scenarios`, each scenario has an eventType.
Write a function that takes in OutputNode[] (all scenarios nodes), read each scenario in `payload.scenarios`, and create an 'event' node for each.
Each 'event' node should have the following structure:
{
  "type": "event",
  "payload": {
    "eventType": "eventType from the scenario"
  },
  "children": [
    {
      "type": "condition",
      "payload": {
        // the condition from the scenario ..
      },
      "children": [
        // list of children of the original scenarios node
      ]
    }
  ]
}

If a scenario doesn't have any condition, then its corresponding event node doesn't need to have the additional condition node created as its children

Then the function should return the list of derived event nodes (OutputNode[])
*/

/**
 * Creates event nodes from scenarios nodes
 * @param scenariosNodes - Array of scenarios nodes
 * @returns Array of event nodes derived from the scenarios
 */
export function createEventNodes(scenariosNodes: OutputNode[]): OutputNode[] {
  const eventNodes: OutputNode[] = [];

  for (const scenariosNode of scenariosNodes) {
    // Validate that this is a scenarios node with the expected structure
    if (scenariosNode.type !== 'scenarios' || !scenariosNode.payload.scenarios) {
      continue;
    }

    const scenarios = scenariosNode.payload.scenarios;

    for (const scenario of scenarios) {
      if (!scenario.eventType) {
        continue; // Skip scenarios without eventType
      }

      const eventNode: OutputNode = {
        type: visualNodeTypes.event,
        payload: {
          eventType: scenario.eventType
        },
        trackingParams: scenariosNode.trackingParams
      };

      // If the scenario has a condition, create a condition node as child
      if (scenario.condition) {
        const conditionNode: OutputNode = {
          type: visualNodeTypes.condition,
          payload: scenario.condition,
          trackingParams: scenariosNode.trackingParams
        };

        // Add the original children of the scenarios node to the condition node
        if (scenariosNode.children) {
          conditionNode.children = [...scenariosNode.children];
        }

        eventNode.children = [conditionNode];
      } else {
        // If no condition, the event node gets the original children directly
        if (scenariosNode.children) {
          eventNode.children = [...scenariosNode.children];
        }
      }

      eventNodes.push(eventNode);
    }
  }

  return eventNodes;
}

/*
A node tree might have multiple branches (one node may have multiple children)
Write a function getPaths(inputNode: OutputNode) []OutputNode
that parses all the paths (rootNode -> end) in the node tree that starts with inputNode, and returns them
*/

/**
 * Finds all paths from the root node to leaf nodes in the tree
 * @param inputNode - The root node to start from
 * @returns Array of tree structures, where each tree represents a linear path from root to leaf
 */
export function getPaths(inputNode: OutputNode): ListNode[] {
  const paths: ListNode[] = [];

  // If this node has no children, it's a leaf node - return a copy of this node
  if (!inputNode.children || inputNode.children.length === 0) {
    return [{
      type: inputNode.type,
      payload: inputNode.payload,
      trackingParams: inputNode.trackingParams
      // No next for leaf nodes
    }];
  }

  // For each child, get all paths starting from that child
  for (const child of inputNode.children) {
    const childPaths = getPaths(child);

    // Create a new list node for each child path
    for (const childPath of childPaths) {
      const pathNode: ListNode = {
        type: inputNode.type,
        payload: inputNode.payload,
        trackingParams: inputNode.trackingParams,
        next: childPath // Each path node has only one next node
      };
      paths.push(pathNode);
    }
  }

  return paths;
}

/*
Each condition node may have nested conditions that looks like this:
{
  "conditions": [
    {
      "lhs": "var.passenger.ID",
      "operator": "eq",
      "rhs": 91718782
    },
    {
      "lhs": "var.food.cutlery.option",
      "operator": "eq",
      "rhs": 1
    }
  ],
  "operator": "and"
}

For such condition node nested under "and" operator, we'd like to break it into multiple singleCondition nodes, and link them together
{
  "type": "condition",
  "payload": {
    "lhs": "var.passenger.ID",
    "operator": "eq",
    "rhs": 91718782
  },
  "children": [
    {
      "type": "condition",
      "payload": {
        "lhs": "var.food.cutlery.option",
        "operator": "eq",
        "rhs": 1
      }
    }
  ]
}

Write a function that takes in a root node (type: OutputNode), break each condition node with nested conditions in the way as mentioned above, then return back the root node
*/

/**
 * Breaks down condition nodes with nested "and" conditions into chains of single condition nodes
 * @param rootNode - The root node to process (guaranteed to be type 'event')
 * @returns The processed root node with condition nodes broken down
 */
export function breakdownConditionNodes(rootNode: ListNode): ListNode {
  // Helper function to recursively flatten a condition payload
  const flattenConditionPayload = (payload: any): any[] => {
    // If this is a condition with "and" operator and multiple conditions, flatten it
    if (payload.conditions &&
        Array.isArray(payload.conditions) &&
        payload.operator === 'and' &&
        payload.conditions.length > 1) {

      const flattened: any[] = [];

      // Recursively flatten each condition
      for (const condition of payload.conditions) {
        const subFlattened = flattenConditionPayload(condition);
        flattened.push(...subFlattened);
      }

      return flattened;
    }

    // If it's a single condition or not an "and" condition, return as-is
    return [payload];
  };

  // Helper function to process individual nodes
  const processNode = (node: ListNode): ListNode => {
    // Process this node if it's a condition node with nested conditions
    if (node.type === visualNodeTypes.condition &&
        node.payload.conditions &&
        Array.isArray(node.payload.conditions) &&
        node.payload.operator === 'and' &&
        node.payload.conditions.length > 1) {

      const originalNext = node.next;

      // Recursively flatten all nested conditions
      const flattenedConditions = flattenConditionPayload(node.payload);

      // Create the first condition node (which will be the new root of this chain)
      const firstConditionNode: ListNode = {
        type: visualNodeTypes.condition,
        payload: flattenedConditions[0],
        trackingParams: node.trackingParams
      };

      // Create a chain of condition nodes for all flattened conditions
      let currentNode = firstConditionNode;
      for (let i = 1; i < flattenedConditions.length; i++) {
        const nextConditionNode: ListNode = {
          type: visualNodeTypes.condition,
          payload: flattenedConditions[i],
          trackingParams: node.trackingParams
        };

        currentNode.next = nextConditionNode;
        currentNode = nextConditionNode;
      }

      // The last condition node gets the original next node (processed recursively)
      if (originalNext) {
        currentNode.next = processNode(originalNext);
      }

      return firstConditionNode;
    }

    // For non-condition nodes or condition nodes that don't need breakdown,
    // just process the next node recursively
    const processedNode: ListNode = {
      type: node.type,
      payload: node.payload,
      trackingParams: node.trackingParams
    };

    if (node.next) {
      processedNode.next = processNode(node.next);
    }

    return processedNode;
  };

  // Since root is guaranteed to be 'event', just process its next node
  const processedRoot: ListNode = {
    type: rootNode.type,
    payload: rootNode.payload,
    trackingParams: rootNode.trackingParams
  };

  if (rootNode.next) {
    processedRoot.next = processNode(rootNode.next);
  }

  return processedRoot;
}

/*
Within a node tree, there multiple consecutive condition nodes. For every segment of consecutive condition nodes, we'd like to sort them.
You can use a hash function to hash the condition node payload, and use that to sort asc.
Write a function to do that: function sortConditions(rootNode: OutputNode) OutputNode
It takes in a root node (event node) and output the root node of the resulting tree
*/

/**
 * Simple hash function for objects
 * @param obj - Object to hash
 * @returns String hash of the object
 */
function hashObject(obj: any): string {
  const str = JSON.stringify(obj, Object.keys(obj).sort());
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return hash.toString();
}

/**
 * Sorts consecutive condition nodes in a tree by condition and lhs occurrence counts
 * @param rootNode - The root node to process (guaranteed to be type 'event')
 * @param conditionOccurrences - Map of stringified conditions to their occurrence counts
 * @param lhsOccurrences - Map of lhs values to their occurrence counts
 * @returns The processed root node with condition nodes sorted
 */
export function sortConditions(rootNode: ListNode, conditionOccurrences: Map<string, number>, lhsOccurrences: Map<string, number>): ListNode {
  // Helper function to process individual nodes
  const processNode = (node: ListNode): ListNode => {
    // If this is a condition node, we need to collect consecutive condition nodes
    if (node.type === visualNodeTypes.condition) {
      const conditionNodes: ListNode[] = [];
      let currentNode: ListNode | undefined = node;

      // Collect all consecutive condition nodes
      while (currentNode && currentNode.type === visualNodeTypes.condition) {
        conditionNodes.push({
          type: currentNode.type,
          payload: currentNode.payload,
          trackingParams: currentNode.trackingParams
        });

        // Move to the next node
        currentNode = currentNode.next;
      }

      // Sort condition nodes by the new criteria
      conditionNodes.sort((a, b) => {
        const lhsA = a.payload.lhs || '';
        const lhsB = b.payload.lhs || '';
        const rhsA = a.payload.rhs || '';
        const rhsB = b.payload.rhs || '';

        const conditionStringA = JSON.stringify(a.payload);
        const conditionStringB = JSON.stringify(b.payload);

        const conditionCountA = conditionOccurrences.get(conditionStringA) || 0;
        const conditionCountB = conditionOccurrences.get(conditionStringB) || 0;

        const lhsCountA = lhsOccurrences.get(lhsA) || 0;
        const lhsCountB = lhsOccurrences.get(lhsB) || 0;

        // First sort by condition occurrence count (desc)
        if (conditionCountA !== conditionCountB) {
          return conditionCountB - conditionCountA;
        }

        // Then sort by lhs occurrence count (desc)
        if (lhsCountA !== lhsCountB) {
          return lhsCountB - lhsCountA;
        }

        // For condition nodes with the same lhs, sort by rhs (asc)
        return String(rhsA).localeCompare(String(rhsB));
      });

      // Reconstruct the chain of sorted condition nodes
      let sortedChain: ListNode | null = null;
      let lastNode: ListNode | null = null;

      for (const conditionNode of conditionNodes) {
        const newNode: ListNode = {
          type: conditionNode.type,
          payload: conditionNode.payload,
          trackingParams: conditionNode.trackingParams
        };

        if (!sortedChain) {
          // First node becomes the root of the chain
          sortedChain = newNode;
          lastNode = newNode;
        } else {
          // Link to the previous node
          lastNode.next = newNode;
          lastNode = newNode;
        }
      }

      // If there's a non-condition node after the consecutive conditions, process it
      if (currentNode) {
        const processedRemainder = processNode(currentNode);

        // Attach the processed remainder to the end of the sorted chain
        if (lastNode) {
          lastNode.next = processedRemainder;
        }
      }

      return sortedChain || node;
    }

    // For non-condition nodes, just process next node recursively
    const processedNode: ListNode = {
      type: node.type,
      payload: node.payload,
      trackingParams: node.trackingParams
    };

    if (node.next) {
      processedNode.next = processNode(node.next);
    }

    return processedNode;
  };

  // Since root is guaranteed to be 'event', just process its next node
  const processedRoot: ListNode = {
    type: rootNode.type,
    payload: rootNode.payload,
    trackingParams: rootNode.trackingParams
  };

  if (rootNode.next) {
    processedRoot.next = processNode(rootNode.next);
  }

  return processedRoot;
}


/*
Write a function that takes in a list of root nodes (each represent a single-chain tree, the root node of each tree are guaranteed to be the same, except tracking params)
The function should merge the common prefix nodes recursively (two nodes are considered equal if their payload (except tracking params) are totally the same) and output the root node of the resulting tree
function mergePrefixes(rootNodes []ListNode) VisualNode

Example:
input (trackingParams are omitted):
rootNode: [
  {"type": "event", "payload": {"eventType": "abc"}} --> {"type": "condition", "payload": "aaa"} --> {"type": "condition", "payload": "bbb"} --> {"type": "actions", "payload": {...}}
  {"type": "event", "payload": {"eventType": "abc"}} --> {"type": "condition", "payload": "aaa"} --> {"type": "condition", "payload": "ccc"} --> {"type": "actions", "payload": {...}}
  {"type": "event", "payload": {"eventType": "abc"}} --> {"type": "condition", "payload": "xyz"} --> {"type": "actions", "payload": {...}}
]

output:
{
  "type": "event",
  "payload": {
    "eventType": "abc"
  },
  "linkedCampaigns": [
    // list of unique (campaignID, countryID) from the corresponding nodes in the input
  ]
  "children": [
    {
      "type": "condition",
      "payload": "aaa",
      "linkedCampaigns": [
        // list of unique (campaignID, countryID) from the corresponding nodes in the input
      ],
      "children": [
        {
          "type": "condition",
          "payload": "bbb",
          "linkedCampaigns": [
            // list of unique (campaignID, countryID) from the corresponding nodes in the input
          ],
          "children": [
            {"type": "actions", "payload": {...}, "linkedCampaigns": [...]}
          ]
        },
        {
          "type": "condition",
          "payload": "ccc",
          "linkedCampaigns": [
            // list of unique (campaignID, countryID) from the corresponding nodes in the input
          ],
          "children": [
            {"type": "actions", "payload": {...}, "linkedCampaigns": [...]}
          ]
        }
      ]
    },
    {
      "type": "condition",
      "payload": "xyz",
      "linkedCampaigns": [
        // list of unique (campaignID, countryID) from the corresponding nodes in the input
      ],
      "children": [
        {"type": "actions", "payload": {...}, "linkedCampaigns": [...]}
      ]
    }
  ]
}
*/

/**
 * Helper function to check if two nodes are equal based on type and payload
 * @param a - First node to compare
 * @param b - Second node to compare
 * @returns True if nodes have same type and payload
 */
function nodesEqual(a: ListNode, b: ListNode): boolean {
  return a.type === b.type && JSON.stringify(a.payload) === JSON.stringify(b.payload);
}

/**
 * Helper function to collect unique campaign metadata from nodes
 * @param nodes - Array of nodes to extract campaigns from
 * @returns Array of unique campaign metadata
 */
function collectUniqueCampaigns(nodes: ListNode[]): CampaignMeta[] {
  const campaignSet = new Set();
  const campaigns: CampaignMeta[] = [];

  for (const node of nodes) {
    if (node.trackingParams) {
      const key = `${node.trackingParams.campaignID}-${node.trackingParams.countryID}`;
      if (!campaignSet.has(key)) {
        campaignSet.add(key);
        campaigns.push({
          campaignID: node.trackingParams.campaignID,
          countryID: node.trackingParams.countryID,
          campaignName: node.trackingParams.campaignName
        });
      }
    }
  }

  return campaigns;
}

/**
 * Merges common prefix nodes from multiple linear chains into a tree structure
 * @param rootNodes - Array of root nodes representing linear chains
 * @param options - options on how to merge the nodes
 * @returns Merged tree structure with common prefixes consolidated
 */
export function mergePrefixes(rootNodes: ListNode[], options?: MergeOptions): VisualNode {
  if (rootNodes.length === 0) {
    throw new Error("At least one root node is required");
  }

  // All root nodes should have the same type and payload
  const firstRoot = rootNodes[0];
  const allRootNodesEqual = rootNodes.every(item => JSON.stringify(item.payload) === JSON.stringify(firstRoot.payload))

  // Create the merged node for the current level
  let mergedNode: VisualNode = {
    type: firstRoot.type,
    payload: firstRoot.payload,
    linkedCampaigns: collectUniqueCampaigns(rootNodes)
  };

  // forming collapsed condition node (formed by conditions sharing the same LHS)
  if (!allRootNodesEqual && firstRoot.type === visualNodeTypes.condition) {
    mergedNode = {
      type: visualNodeTypes.collapsedCondition,
      payload: {
        variable: firstRoot.payload.lhs,
        conditions: rootNodes.map(node => node.payload)
      },
      linkedCampaigns: collectUniqueCampaigns(rootNodes)
    };
  }

  // Group next nodes by their type+payload combination
  const nextNodesMap = new Map();

  for (const rootNode of rootNodes) {
    if (rootNode.next) {
      const nextNode = rootNode.next;
      let key = `${nextNode.type}|${JSON.stringify(nextNode.payload)}`;

      // optional logic: group the condition nodes so long they share the same LHS
      if (options.mergeConditionsByLHS && nextNode.type === visualNodeTypes.condition
        && nextNode.payload.lhs !== '') {
        key = `${visualNodeTypes.collapsedCondition}|${nextNode.payload.lhs}`;
      }

      if (!nextNodesMap.has(key)) {
        nextNodesMap.set(key, []);
      }
      nextNodesMap.get(key).push(rootNode.next);
    }
  }

  // Recursively merge each group of next nodes
  if (nextNodesMap.size > 0) {
    mergedNode.children = [];
    const nextNodesGroups = Array.from(nextNodesMap.values());
    for (const nextNodes of nextNodesGroups) {
      const mergedChild = mergePrefixes(nextNodes, options);
      mergedNode.children.push(mergedChild);
    }
  }

  return mergedNode;
}

/*
Write a function that takes in a list of campaigns and output the converted visual tree.
function visualiseCampaigns(campaigns: Campaign[], eventType: string) VisualNode

It should do the following:
- for each campaign:
  - call convertNodeTree --> get []scenarios TODO
  - then call createEventNodes --> []event TODO
  - filter out the nodes that don't match `eventType`
  - call getPaths --> []ListNode
  - for each ListNode from last step, call breakdownConditionNodes --> []ListNode
- collect all the ListNode obtained from all the campaigns
  - count the occurrences of different conditions from all the condition nodes (two conditions are considered the same if their stringified text are the same)
  - then sort the condition nodes in each node list using the following rule:
    - first sort by the number of occurrences of the condition (desc)
    - then sort by the number of occurrences of the lhs (desc)
    - for condition nodes with the same lhs, sort by rhs (asc)
  - call mergePrefixTree and return VisualNode
*/

/**
 * Converts a list of campaigns into a merged visual tree
 * @param campaigns - Array of campaigns to process
 * @param eventType - Event type to filter by
 * @returns Merged visual tree representation
 */
export function visualiseCampaigns(campaigns: Campaign[], eventType: string, options?: VisualiseOptions): VisualNode {
  const allProcessedPaths: ListNode[] = [];

  for (const campaign of campaigns) {
    // Create campaign metadata for tracking
    const campaignMeta: CampaignMeta = {
      campaignID: campaign.campaignID,
      countryID: campaign.countryID,
      campaignName: campaign.name
    };

    // Step 1: Convert node tree to get scenarios nodes
    const scenariosNodes = convertNodeTree(campaign.originalNodes, campaignMeta);

    // Step 2: Create event nodes from scenarios
    const eventNodes = createEventNodes(scenariosNodes);

    // Step 3: Filter event nodes by eventType
    const filteredEventNodes = eventNodes.filter(node =>
      node.payload.eventType === eventType
    );

    // Step 4: Get paths for each filtered event node
    for (const eventNode of filteredEventNodes) {
      const paths = getPaths(eventNode);

      // Step 5: Process each path through breakdown (no sorting yet)
      for (const path of paths) {
        // Step 5a: Breakdown condition nodes
        const brokenDownPath = breakdownConditionNodes(path);

        allProcessedPaths.push(brokenDownPath);
      }
    }
  }

  if (allProcessedPaths.length === 0) {
    throw new Error(`No paths found for eventType: ${eventType}`);
  }

  // Step 6: Count occurrences of different conditions and lhs from all condition nodes
  const conditionOccurrences = new Map<string, number>();
  const lhsOccurrences = new Map<string, number>();

  for (const path of allProcessedPaths) {
    let currentNode: ListNode | undefined = path;
    while (currentNode) {
      if (currentNode.type === visualNodeTypes.condition) {
        // Count condition occurrences (stringified)
        const conditionString = JSON.stringify(currentNode.payload);
        conditionOccurrences.set(conditionString, (conditionOccurrences.get(conditionString) || 0) + 1);

        // Count lhs occurrences
        if (currentNode.payload.lhs) {
          const lhs = currentNode.payload.lhs;
          lhsOccurrences.set(lhs, (lhsOccurrences.get(lhs) || 0) + 1);
        }
      }
      currentNode = currentNode.next;
    }
  }

  // Step 7: Sort condition nodes in each path using the new criteria
  const sortedPaths = allProcessedPaths.map(path => sortConditions(path, conditionOccurrences, lhsOccurrences));

  // Step 8: Merge all processed paths into a single visual tree
  return mergePrefixes(sortedPaths, {mergeConditionsByLHS: (options ? options.mergeConditionsByLHS : false)});
}
